import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import AvatarText from 'ui-library/avatar/AvatarText';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';

import {AvatarSize} from './avatar/AvatarConstants';
import {AvatarFrameType} from './avatar/AvatarFrame';

interface PartyWithAvatar extends HasAvatar {
  id: string;
}

interface BaseAvatarProps {
  size: AvatarSize;
  className?: string;
  tooltipPlacement?: FloatingPlacement;
}

interface SinglePartyAvatarProps extends BaseAvatarProps {
  party: PartyWithAvatar;
  frame?: AvatarFrameType;
}

interface OverflowAvatarProps extends BaseAvatarProps {
  parties: Array<PartyWithAvatar>;
  dataTestAttrs?: DataTestAttrs;
}

interface PartyAvatarListProps extends BaseAvatarProps {
  avatarClassName?: string;
  avatarTextDataTestAttrs?: DataTestAttrs;
  parties: Array<PartyWithAvatar>;
  maxAvatars: number;
  framesByPartyId?: Record<string, AvatarFrameType>;
}

export function SinglePartyAvatar({
  tooltipPlacement,
  party,
  className,
  size,
  frame,
}: SinglePartyAvatarProps): JSX.Element {
  return (
    <Tooltip
      placement={tooltipPlacement}
      text={getPartyDisplayString(party)}
      isDisabled={tooltipPlacement == null}
    >
      <Avatar className={className} entity={party} size={size} frame={frame} />
    </Tooltip>
  );
}

export function OverflowAvatar({
  tooltipPlacement,
  parties,
  className,
  size,
  dataTestAttrs,
}: OverflowAvatarProps): JSX.Element {
  return (
    <Tooltip
      placement={tooltipPlacement}
      text={parties.map((party) => getPartyDisplayString(party)).join(', ')}
    >
      <AvatarText
        className={className}
        backgroundColor={Colors.GRAY_DARKER}
        size={size}
        text={`+${parties.length}`}
        dataTestAttrs={dataTestAttrs}
      />
    </Tooltip>
  );
}

function getAvatarList(
  parties: Array<PartyWithAvatar>,
  maxAvatars: number,
  size: AvatarSize,
  className?: string,
  tooltipPlacement?: FloatingPlacement,
  framesByPartyId: Record<string, AvatarFrameType> = {},
  avatarTextDataTestAttrs?: DataTestAttrs
): Array<JSX.Element> {
  if (parties.length > maxAvatars) {
    const firstParties = parties.slice(0, maxAvatars - 1);
    const remainingParties = parties.slice(maxAvatars - 1);
    const firstAvatars = firstParties.map((party) => (
      <SinglePartyAvatar
        key={party.id}
        className={className}
        party={party}
        size={size}
        tooltipPlacement={tooltipPlacement}
        frame={framesByPartyId[party.id]}
      />
    ));
    const lastAvatar = (
      <OverflowAvatar
        key="overflow-avatar"
        className={className}
        parties={remainingParties}
        size={size}
        tooltipPlacement={tooltipPlacement}
        dataTestAttrs={avatarTextDataTestAttrs}
      />
    );
    return firstAvatars.concat([lastAvatar]);
  }
  return parties.map((party) => (
    <SinglePartyAvatar
      key={party.id}
      className={className}
      party={party}
      size={size}
      tooltipPlacement={tooltipPlacement}
      frame={framesByPartyId[party.id]}
    />
  ));
}

export default function PartyAvatarList({
  parties,
  maxAvatars,
  size,
  className,
  avatarClassName,
  avatarTextDataTestAttrs,
  tooltipPlacement,
  framesByPartyId,
}: PartyAvatarListProps): JSX.Element {
  return (
    <div
      className={classNames(css(framesByPartyId ? styles.framedAvatarList : styles.avatarList), className)}
    >
      {getAvatarList(
        parties,
        maxAvatars,
        size,
        avatarClassName,
        tooltipPlacement,
        framesByPartyId,
        avatarTextDataTestAttrs
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  avatarList: {
    display: 'inline-flex',
    gap: '4px',
  },
  framedAvatarList: {
    display: 'inline-flex',
    gap: '8px',
  },
});
